import { FC } from "react";
import { Link } from "react-router";
import { COMPLIANCE_CHECK_DOCUMENT_CONFIGS, DOCUMENT_CONFIGS } from "../../../../models/superadmin";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { useAppSelector as useSelector } from "../../../../store";
import { formatListWithAnd } from "../../../../utils/documentHelper";
import { isFirstAidOptional } from "../../../../utils/missingDocsHelper";
import Warning from "../../../UI/Icons/Warning";

interface ExpiringNotificationsProps {
    isShowExpiringDocAlert?: boolean;
    isJobListing?: boolean;
}

const ExpiringNotifications: FC<ExpiringNotificationsProps> = ({
    isShowExpiringDocAlert,
    isJobListing = false
}) => {
    const documentsExpiresInOneMonth = useSelector(
        (state) => state.onboarding.userpersonaldetails?.documents_expire_one_month
    );
    const isDocumentsExpired = useSelector(
        (state) => state.onboarding.userpersonaldetails?.is_documents_expired
    );
    const expiringDocumentsCollections = useSelector(
        (state) => state.onboarding.userpersonaldetails?.documents_expire_one_month_documents
    );
    const expiringAfteraMonthDocumentsCollections = useSelector(
        (state) => state.candidateJobs.candidateJobsList?.documents_expire_after_one_month_documents
    );
    const qualifications = useSelector(
        (state) => state.onboarding.userpersonaldetails?.qualifications
    );
    const firstAidOptional = isFirstAidOptional(qualifications);

    const allDocsType = [...DOCUMENT_CONFIGS, ...COMPLIANCE_CHECK_DOCUMENT_CONFIGS];

    const formatDateToReadable = (dateStr: string): string | null => {
        if (!dateStr) return null;

        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }

    type DocWithDate = { title: string; date: string };

    const matchedMultiple: DocWithDate[] = [];
    const expired: DocWithDate[] = [];

    /** this we added for showing the alert in top of the joblisting */
    expiringDocumentsCollections?.forEach((doc) => {
        const match = allDocsType.find((config) => config.type === doc.document_type);

        if (!match) return;
        if (firstAidOptional && doc.document_type === "first_aid_certificate") return;

        const title = match.title;
        const isExpired = doc.is_expired === 1;

        if (isExpired) {
            expired.push({ title, date: doc.validity_date });
        } else {
            matchedMultiple.push({ title, date: doc.validity_date });
        }
    });

    const matchedMultipleAfteraMonth: DocWithDate[] = [];
    const expiredAfteraMonth: DocWithDate[] = [];

    /** this we added for showing the alert inbetween in the joblisting */
    expiringAfteraMonthDocumentsCollections?.forEach((doc) => {
        const match = allDocsType.find((config) => config.type === doc.document_type);

        if (!match) return;
        if (firstAidOptional && doc.document_type === "first_aid_certificate") return;

        const title = match.title;
        const isExpired = doc.is_expired === 1;

        // if (isExpired) {
        //     expiredAfteraMonth.push({ title, date: doc.validity_date });
        // } else {
            matchedMultipleAfteraMonth.push({ title, date: doc.validity_date });
        // }
    });

    // Sort by earliest date
    const sortByDate = (a: DocWithDate, b: DocWithDate) => new Date(a.date).getTime() - new Date(b.date).getTime();

    expired.sort(sortByDate);
    matchedMultiple.sort(sortByDate);

    const formattedExpired = formatListWithAnd(expired.map((e) => e.title));
    const formattedMultipleTitles = formatListWithAnd(
        matchedMultiple.map(
            (m) => `${m.title} needs to be renewed by ${formatDateToReadable(m.date)}`
        )
    );

    expiredAfteraMonth.sort(sortByDate);
    matchedMultipleAfteraMonth.sort(sortByDate);

    const formattedMultipleTitlesAfteraMonth = formatListWithAnd(
        matchedMultipleAfteraMonth.map(
            (m) => `${m.title} needs to be renewed by ${formatDateToReadable(m.date)}`
        )
    );

    return (
        <>
            { (isShowExpiringDocAlert && formattedMultipleTitlesAfteraMonth.length > 0) ?
                <div className="w-100 mobile-w-100 bg-off-white px-15px py-10px radius-8px d-flex gap-6 align-items-center justify-content-center">
                    <Warning />
                    <div className="fs-13 text-black fw-400">
                        Your{" "}{formattedMultipleTitlesAfteraMonth}.{" "}Jobs after this date will become visible once you've renewed.
                    </div>
                </div>
            : ((documentsExpiresInOneMonth || isDocumentsExpired) && (formattedMultipleTitles.length > 0 || formattedExpired.length > 0) && !isJobListing) ? 
                <div className="d-flex w-100 max-w-674px">
                    <div className="bg-alert-light px-15px py-10px radius-8px d-flex gap-6">
                        <Warning />
                        <div className="fs-13 text-black fw-400">
                            Your {" "}
                            {formattedExpired.length > 0 && formattedMultipleTitles.length > 0 ? (
                                <>
                                    <span className="fw-400">{formattedExpired}</span> needs to be renewed and{" "}
                                    <span className="fw-400">{formattedMultipleTitles}</span>.
                                </>
                            ) : formattedExpired.length > 0 ? (
                                <>
                                    <span className="fw-400">{formattedExpired}</span> needs to be renewed.
                                </>
                            ) : formattedMultipleTitles.length > 0 ? (
                                <>
                                    <span className="fw-400">{formattedMultipleTitles}</span>.
                                </>
                            ) : (
                                ""
                            )}{" "}
                            Please ensure this is updated in order to continue searching and accepting jobs on Connected Crew.{" "}
                            <Link className="text-black text-decoration-underline fw-700 fs-13" to={AUTH_ROUTE.MYPROFILE_COMPLETED}>Go to My Profile</Link>
                        </div>
                    </div>
                </div>
             : "" }
        </>
    )
}

export default ExpiringNotifications;